import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Clock, CheckCircle2, AlertCircle, Loader, User } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_INFO = {
  waiting:     { label: 'Waiting',     color: '#f97316', bg: '#fff7ed', icon: Clock },
  'in-progress':{ label: 'With Doctor', color: '#2563eb', bg: '#eff6ff', icon: User },
  completed:   { label: 'Completed',   color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle2 },
  cancelled:   { label: 'Cancelled',   color: '#dc2626', bg: '#fef2f2', icon: AlertCircle },
};

export default function PatientPortalPage() {
  const [phone, setPhone]     = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [time, setTime]       = useState(new Date());

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-check if patientUID in URL (from QR code)
  useEffect(() => {
    const uid = new URLSearchParams(window.location.search).get('uid');
    if (uid) checkByUID(uid);
  }, []);  // eslint-disable-line

  async function checkByUID(uid) {
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await axios.get(`${API}/api/qr/status/${uid}`);
      setResult(res.data);
    } catch {
      setError('Could not fetch queue status. Please try again.');
    } finally { setLoading(false); }
  }

  async function handlePhoneCheck(e) {
    e.preventDefault();
    if (!phone.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await axios.get(`${API}/api/qr/lookup`, { params: { phone } });
      setResult(res.data);
    } catch {
      setError('Could not fetch queue status. Please try again.');
    } finally { setLoading(false); }
  }

  const si = result?.patient ? (STATUS_INFO[result.patient.status] || STATUS_INFO.waiting) : null;
  const StatusIcon = si?.icon || Clock;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0f1923 0%,#1a3255 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '32px 16px', fontFamily: 'system-ui,sans-serif' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🦷</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'white', letterSpacing: '-0.02em' }}>
          Queue Status
        </div>
        <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
          {time.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          {' · '}
          {time.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
        </div>
      </div>

      {/* Card */}
      <div className="portal-card" style={{ width: '100%', maxWidth: 420, background: 'white', borderRadius: 20, padding: 28, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>

        {/* Phone lookup form */}
        <form onSubmit={handlePhoneCheck} style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Enter your phone number
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="e.g. 9876543210"
              style={{ flex: 1, padding: '12px 16px', borderRadius: 10, border: '2px solid #e2e8f0', fontSize: 16, outline: 'none', fontFamily: 'inherit' }}
              onFocus={e => e.target.style.borderColor = '#2563eb'}
              onBlur={e => e.target.style.borderColor = '#e2e8f0'}
            />
            <button
              type="submit"
              disabled={loading}
              style={{ padding: '12px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 14 }}>
              {loading ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={16} />}
              Check
            </button>
          </div>
        </form>

        {/* Error */}
        {error && (
          <div style={{ padding: '12px 16px', background: '#fef2f2', borderRadius: 10, color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Not found */}
        {result && !result.found && (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#64748b' }}>
            <AlertCircle size={36} style={{ marginBottom: 10, opacity: 0.5 }} />
            <div style={{ fontWeight: 600 }}>No token found for today</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Please check your phone number or visit the reception.</div>
          </div>
        )}

        {/* Found — token status */}
        {result?.found && result.patient && (
          <div>
            {/* Status badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', background: si.bg, borderRadius: 14, marginBottom: 20 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: si.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <StatusIcon size={24} color="white" />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 18, color: si.color }}>{si.label}</div>
                {result.patient.status === 'waiting' && result.waitingBefore !== undefined && (
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    {result.waitingBefore === 0
                      ? 'You are next! 🎉'
                      : `${result.waitingBefore} patient${result.waitingBefore > 1 ? 's' : ''} ahead of you`}
                  </div>
                )}
              </div>
            </div>

            {/* Token details */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              {[
                ['Token Number', `#${result.patient.tokenNumber}`],
                ['Patient Name', result.patient.name],
                ['Patient ID',   result.patient.patientUID],
                result.patient.appointmentSlot
                  ? ['Appointment', result.patient.appointmentSlot]
                  : null,
                result.patient.doctorName
                  ? ['Doctor', result.patient.doctorName]
                  : null,
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: 10 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Bill summary */}
            {result.billing && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  Bill Summary
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  <div style={{ padding: '12px 10px', background: '#f8fafc', borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>TOTAL</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>₹{result.billing.total.toLocaleString('en-IN')}</div>
                  </div>
                  <div style={{ padding: '12px 10px', background: '#f0fdf4', borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 700 }}>PAID</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#16a34a' }}>₹{result.billing.paid.toLocaleString('en-IN')}</div>
                  </div>
                  <div style={{ padding: '12px 10px', background: result.billing.pending > 0 ? '#fff7ed' : '#f8fafc', borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: result.billing.pending > 0 ? '#f97316' : '#94a3b8', fontWeight: 700 }}>PENDING</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: result.billing.pending > 0 ? '#f97316' : '#94a3b8' }}>₹{result.billing.pending.toLocaleString('en-IN')}</div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => checkByUID(result.patient.patientUID)}
              style={{ width: '100%', padding: '12px 0', background: '#f1f5f9', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Loader size={14} /> Refresh Status
            </button>
          </div>
        )}

        <div style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 20, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
          Scan the QR code at reception or enter your phone number above
        </div>
      </div>

    </div>
  );
}